import { Spinner } from '@consigli/facade';
import { type useInfiniteScroll } from '@consigli/hooks';
import { type FC, type ReactNode, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface AddContextListRegionProps {
  children: ReactNode;
  emptyMessage: string;
  /**
   * A table header. With one, the rows sit in a bordered frame under it and
   * only the rows scroll, so the header never moves.
   */
  header?: ReactNode;
  hasError: boolean;
  isEmpty: boolean;
  isFetchingMore: boolean;
  isLoading: boolean;
  onScroll: (event: UIEvent<HTMLElement>) => void;
  /** Sentinel the infinite-scroll observer watches. */
  triggerRef: ReturnType<typeof useInfiniteScroll>;
}

/**
 * The scrolling half of the modal body: the rows, plus the two things that pull
 * the next chunk in. `onScroll` asks for it halfway down the loaded rows. The
 * sentinel covers a list too short to scroll. The sentinel is inside the
 * scroll container, which is what
 * `useInfiniteScroll` needs - it observes against the viewport, and an element
 * scrolled out of a clipping ancestor does not intersect it.
 */
export const AddContextListRegion: FC<AddContextListRegionProps> = ({
  children,
  emptyMessage,
  header,
  hasError,
  isEmpty,
  isFetchingMore,
  isLoading,
  onScroll,
  triggerRef,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
        <span className="sr-only">{t('prop-chat.add-context-loading')}</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <p className="flex flex-1 items-center justify-center text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-secondary)]">
        {t('prop-chat.add-context-error')}
      </p>
    );
  }

  if (isEmpty) {
    return (
      <p className="flex flex-1 items-center justify-center text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-secondary)]">
        {emptyMessage}
      </p>
    );
  }

  const footer = (
    <>
      <div ref={triggerRef} aria-hidden="true" className="h-px shrink-0" />
      {isFetchingMore && (
        <div className="flex shrink-0 items-center justify-center py-[var(--ds-space-3)]">
          <Spinner />
          <span className="sr-only">{t('prop-chat.add-context-loading')}</span>
        </div>
      )}
    </>
  );

  if (header !== undefined) {
    return (
      // The modal body has no bottom padding, so card rows scroll flush to the
      // footer. A framed table needs the gap back, matching the body's top.
      <div className="mb-[var(--ds-space-4)] flex min-h-0 flex-1 flex-col overflow-clip rounded-[var(--ds-radius-12)] border border-solid border-[var(--ds-color-border-primary)]">
        {header}
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
          data-add-context-scroll
          onScroll={onScroll}
        >
          {children}
          {footer}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-[var(--ds-space-3)] overflow-y-auto overscroll-contain"
      data-add-context-scroll
      onScroll={onScroll}
    >
      {children}
      {footer}
    </div>
  );
};
